import { useEffect } from 'react';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  buildLeadListParams,
  resolveLeadListFilters,
} from '@/components/organisms/leads/LeadListPanel';
import { useLeads } from '@/hooks/leads/useLeads';
import { queryClient } from '@/lib/queryClient';
import { leadKeys } from '@/lib/queryKeys';
import type { LeadListResponse } from '@/types/lead';
import { Route as LeadsLayoutRoute } from '../leads';

function isDesktopRequest() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(min-width: 768px)').matches;
}

function isSplitView() {
  try {
    const storedView = localStorage.getItem('leads-view');
    return storedView !== 'table' && storedView !== 'board';
  } catch {
    return true;
  }
}

export const Route = createFileRoute('/_dashboard/dashboard/leads/')({
  beforeLoad: ({ search }) => {
    if (!isDesktopRequest() || !isSplitView()) return;

    const cachedLists = queryClient.getQueriesData<LeadListResponse>({
      queryKey: leadKeys.lists(),
    });
    const firstLead = cachedLists.flatMap(([, data]) => data?.data ?? []).find((lead) => lead?.id);

    if (firstLead?.id) {
      throw redirect({
        to: '/dashboard/leads/$leadId',
        params: { leadId: firstLead.id },
        search: search as never,
        replace: true,
      });
    }
  },
  component: LeadsIndexPage,
});

function LeadsIndexPage() {
  const router = useRouter();
  const search = LeadsLayoutRoute.useSearch();
  const filters = resolveLeadListFilters(search);
  const query = useLeads(buildLeadListParams({ ...filters, page: 1 }, 1), {
    enabled: isDesktopRequest() && isSplitView(),
  });

  useEffect(() => {
    if (!isDesktopRequest() || !isSplitView()) return;
    const lead = query.data?.data?.[0];
    if (lead?.id) {
      void router.navigate({
        to: '/dashboard/leads/$leadId',
        params: { leadId: lead.id },
        search: search as never,
        replace: true,
      });
    }
  }, [query.data, router, search]);

  if (query.isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-muted-foreground">
      <FileText size={36} />
      <div>
        <p className="text-sm font-medium text-foreground">Select a lead</p>
        <p className="text-xs">Choose a lead from the list to review pipeline details.</p>
      </div>
    </div>
  );
}
